import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ApiUtil from '../utils/apiUtils';
import GrapesJSViewer from './GrapesJSViewer';
import './DynamicPage.css';

const PageBuilderPage = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use prop slug if provided, otherwise extract from params or location
  const slug = propSlug || paramSlug || location.pathname.substring(1);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageBuilderPage = async () => {
      try {
        setLoading(true);
        
        // Skip redirect check for now and go directly to page fetch
        const response = await ApiUtil.SendAsync({
          url: `/page-templates/slug/${slug}`,
          method: 'GET'
        });
        
        if (response.data && response.data.success) {
          const pageData = response.data.data;
          setPage(pageData);
        } else {
          setError('Page not found');
        }
        
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setError('Page not found');
        } else {
          setError('Error loading page');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPageBuilderPage();
    }
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="dynamic-page-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading page...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="dynamic-page-error">
        <div className="error-container">
          <h1>404 - Page Not Found</h1>
          <p>The page you're looking for doesn't exist or has been moved.</p>
          <button 
            className="back-home-btn"
            onClick={() => navigate('/')}
          >
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{page.seoTitle || page.name}</title>
        <meta name="description" content={page.seoDescription || ''} />
        <meta name="keywords" content={page.seoKeywords || ''} />
        <meta property="og:title" content={page.seoTitle || page.name} />
        <meta property="og:description" content={page.seoDescription || ''} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/${slug}`} />
      </Helmet>
      
      <div className="dynamic-page-container">
        {/* Inject the CSS content (includes both component CSS and custom CSS) */}
        {page.cssContent && (
          <style dangerouslySetInnerHTML={{ __html: page.cssContent }} />
        )}
        
        {/* Render PageBuilder content using container layout */}
        <div className="container">
          <div className="dynamic-page-content">
            {/* Try simple HTML rendering first as fallback */}
            {page.htmlContent ? (
              <div className="simple-html-content">
                <div dangerouslySetInnerHTML={{ __html: page.htmlContent }} />
              </div>
            ) : (
              <GrapesJSViewer 
                grapesJsData={page.grapesJsData}
                htmlContent={page.htmlContent}
                cssContent={page.cssContent}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PageBuilderPage;
